import type { Locale, PlannerDraftV1, PlannerMode, TripPlace } from '../types';

const STORAGE_KEY = 'tragenda-planner-draft-v1';
export const PLANNER_DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

function randomId() {
  return typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function isPlace(value: unknown): value is TripPlace {
  if (!value || typeof value !== 'object') return false;
  const place = value as Partial<TripPlace>;
  return typeof place.placeId === 'string' && !!place.placeId && typeof place.title === 'string' && !!place.title
    && typeof place.address === 'string' && typeof place.primaryType === 'string' && typeof place.googleMapsUri === 'string'
    && !!place.location && Number.isFinite(place.location.lat) && Math.abs(place.location.lat) <= 90
    && Number.isFinite(place.location.lng) && Math.abs(place.location.lng) <= 180;
}

function isDate(value: unknown): value is string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T12:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

export function createPlannerDraft(input: {
  mode: PlannerMode;
  locale: Locale;
  title: string;
  destination: TripPlace;
    startDate: string;
    endDate: string;
  }): PlannerDraftV1 {
  const now = Date.now();
  return {
    version: 1,
    id: randomId(),
    idempotencyKey: randomId(),
    ...input,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    stops: [],
    createdAt: now,
    expiresAt: now + PLANNER_DRAFT_TTL_MS,
  };
}

export function savePlannerDraft(draft: PlannerDraftV1) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(draft)); return true; }
  catch { return false; }
}

export function loadPlannerDraft(): PlannerDraftV1 | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const draft = JSON.parse(raw) as Partial<PlannerDraftV1>;
    if (draft.version !== 1 || typeof draft.id !== 'string' || !draft.id || typeof draft.idempotencyKey !== 'string' || !draft.idempotencyKey
      || !['quick_day', 'full_trip'].includes(draft.mode ?? '') || !['en', 'tr', 'sv'].includes(draft.locale ?? '')
      || typeof draft.title !== 'string' || typeof draft.timezone !== 'string' || !isPlace(draft.destination)
      || !isDate(draft.startDate) || !isDate(draft.endDate) || draft.endDate < draft.startDate
      || !Array.isArray(draft.stops) || !draft.stops.every(isPlace)
      || (draft.alternatives !== undefined && (!Array.isArray(draft.alternatives) || !draft.alternatives.every(isPlace)))
      || (draft.legs !== undefined && (!Array.isArray(draft.legs) || !draft.legs.every(leg => !!leg && typeof leg === 'object' && (leg.encodedPolyline === undefined || typeof leg.encodedPolyline === 'string'))))) {
      clearPlannerDraft();
      return null;
    }
    if (!Number.isFinite(draft.createdAt) || !Number.isFinite(draft.expiresAt) || draft.expiresAt! <= Date.now()) {
      clearPlannerDraft();
      return null;
    }
    return draft as PlannerDraftV1;
  } catch {
    clearPlannerDraft();
    return null;
  }
}

export function clearPlannerDraft() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* The browser may disable local storage. */ }
}

export function plannerDraftStorageKey() {
  return STORAGE_KEY;
}
